import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { formatPriceARS } from '@/config/pricing';

// Aviso que reemplaza a "Nueva entrevista" cuando una cuenta con pack no tiene
// entrevistas libres. Existe para que el profe se entere ANTES de empezar, y no
// al tocar "Guardar" despues de haber cargado toda la entrevista con el alumno.
export function AvisoSinCreditos({ saldo }) {
  const navigate = useNavigate();
  return (
    <div
      className="max-w-xl mx-auto mt-6 bg-white border border-amber-200 rounded-2xl p-6 shadow-sm"
      data-testid="aviso-sin-creditos"
    >
      <h2 className="text-lg font-semibold text-[#1F3A5F] font-['Inter']">Te quedaste sin entrevistas</h2>
      <p className="mt-2 text-sm text-slate-600 leading-relaxed font-['Inter']">
        Para cargar una entrevista nueva, comprá otro pack o pasate al ilimitado: te descontamos lo
        que ya pagaste en packs.
      </p>
      {saldo.links_pendientes > 0 && (
        <p className="mt-2 text-sm text-slate-600 leading-relaxed font-['Inter']">
          Tenés {saldo.links_pendientes === 1 ? '1 entrevista reservada' : `${saldo.links_pendientes} entrevistas reservadas`} en
          links que tu alumno todavía no completó. Si el link vence sin usarse, la entrevista vuelve a tu saldo.
        </p>
      )}
      <p className="mt-2 text-sm text-slate-600 leading-relaxed font-['Inter']">
        Los retests de tus alumnos siguen siendo gratis, desde la ficha de cada uno.
      </p>
      <div className="mt-5 flex flex-col sm:flex-row gap-2">
        <button
          type="button"
          onClick={() => navigate('/checkout?producto=ilimitado')}
          className="h-11 px-5 rounded-xl bg-[#4A8126] hover:bg-[#3D6A1F] text-white text-sm font-semibold"
          data-testid="aviso-sin-creditos-ilimitado"
        >
          Pasate al ilimitado por {formatPriceARS(saldo.precio_ilimitado)}
        </button>
        <button
          type="button"
          onClick={() => navigate('/checkout?producto=pack')}
          className="h-11 px-5 rounded-xl border border-slate-300 text-[#1F3A5F] text-sm font-semibold hover:bg-slate-50"
          data-testid="aviso-sin-creditos-pack"
        >
          Comprar otro pack de {saldo.creditos_por_pack} por {formatPriceARS(saldo.precio_pack)}
        </button>
      </div>
    </div>
  );
}

// Saldo de entrevistas en el menu lateral. Solo aparece en cuentas con pack:
// a quien tiene el ilimitado no le suma nada verlo.
//
// Se vuelve a pedir en cada cambio de pantalla: despues de cargar una
// entrevista o generar un link, el saldo tiene que reflejarlo al volver al menu.
export default function SaldoCreditos({ onNavigate }) {
  const [saldo, setSaldo] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let cancelado = false;
    api.getCreditos()
      .then((s) => { if (!cancelado) setSaldo(s); })
      .catch((e) => {
        // No se oculta el error: se registra. El menu sigue andando igual.
        console.error('[SaldoCreditos] no se pudo leer el saldo:', e);
      });
    return () => { cancelado = true; };
  }, [location.pathname]);

  if (!saldo || saldo.tipo !== 'creditos') return null;

  const libres = saldo.creditos_libres ?? 0;
  const pendientes = saldo.links_pendientes ?? 0;
  const ir = (ruta) => {
    if (onNavigate) onNavigate();
    navigate(ruta);
  };

  return (
    <div className="px-3 pb-3" data-testid="saldo-creditos">
      <div className={`p-3 rounded-lg border ${libres === 0 ? 'bg-amber-400/10 border-amber-300/40' : 'bg-white/5 border-white/10'}`}>
        <p className="text-sm font-semibold text-white font-['Inter']" data-testid="saldo-creditos-libres">
          {libres === 0
            ? 'Te quedaste sin entrevistas'
            : `Te ${libres === 1 ? 'queda 1 entrevista' : `quedan ${libres} entrevistas`}`}
        </p>
        {pendientes > 0 && (
          <p className="text-[11px] text-white/60 mt-0.5 font-['Inter']">
            {pendientes === 1 ? '1 reservada' : `${pendientes} reservadas`} en links que tu alumno todavía no completó
          </p>
        )}
        <p className="text-[11px] text-white/50 mt-1 font-['Inter']">
          Los retests y las pruebas funcionales no gastan.
        </p>
        <div className="mt-3 flex flex-col gap-1.5">
          <button
            type="button"
            onClick={() => ir('/checkout?producto=ilimitado')}
            className="w-full text-left text-xs font-semibold text-white bg-[#4A8126] hover:bg-[#3D6A1F] rounded-md px-2.5 py-2 transition-colors"
            data-testid="saldo-creditos-ilimitado"
          >
            Pasate al ilimitado por {formatPriceARS(saldo.precio_ilimitado)}
          </button>
          <button
            type="button"
            onClick={() => ir('/checkout?producto=pack')}
            className="w-full text-left text-xs text-white/80 hover:text-white hover:bg-white/10 rounded-md px-2.5 py-1.5 transition-colors"
            data-testid="saldo-creditos-pack"
          >
            Comprar otro pack de {saldo.creditos_por_pack} por {formatPriceARS(saldo.precio_pack)}
          </button>
        </div>
      </div>
    </div>
  );
}
